<template>
    <div id="clients-page">
        <div class="container-fluid">
            <h1>{{ t('orders-page_title') }}</h1>

            <ConfirmDelete ref="confirmDelete" :count="deleteCount" @confirm="onConfirmDelete" />

            <div>
                <filter-bar
                    ref="filterBar"
                    @changed="updateFilters"
                    :has-branch-filter="canFilterPerBranch"
                    :show-labels="true"
                    :filters="extraFilters"
                />
            </div>

            <div class="clearfix"></div>

            <div v-if="ready" :class="{ 'has-selection': selectedOrders.length > 0 }">
                <grid ref="ordersGrid" source="orders" :filters="filters" :showSearch="false" @loaded="onGridLoaded">
                    <template v-slot:grid-actions>
                        <div class="d-flex align-items-center gap-sm">
                            <KvTooltip :text="t('orders-export')">
                                <button class="btn btn-primary" @click.prevent="exportOrders" :disabled="exporting"
                                    style="display: flex; gap: 4px; align-items: center; justify-content: center;">
                                    <Icon icon-class="ui-download-01" size="5" />
                                    <span>{{ t('orders-export') }}</span>
                                </button>
                            </KvTooltip>

                            <KvPopup v-if="user.global_admin" placement="bottom left" min-width="200px">
                                <template #trigger="{ toggle }">
                                    <KvButton variant="tonal" prepend-icon="ui-dots-vertical"
                                        :aria-label="t('orders-more_actions')" @click="toggle" />
                                </template>
                                <template #content="{ toggle }">
                                    <div class="kv-popup-list">
                                        <KvButton alignment="left" prepend-icon="ui-trash-01" variant="tonal"
                                            @click="openDeleteAll(); toggle()">
                                            {{ t('orders-remove_all') }}
                                        </KvButton>
                                    </div>
                                </template>
                            </KvPopup>
                        </div>
                    </template>

                    <template v-slot:rows="grid">
                        <thead>
                            <tr>
                                <th v-if="user.global_admin" class="checkbox-col" ref="selectAllCell">
                                    <div class="checkbox-center-wrapper">
                                        <button v-if="selectedOrders.length > 0" class="toggle-all-btn"
                                            :class="{ floating: toggleAllFloating }" :style="toggleAllStyle"
                                            :title="allSelectedOnPage ? t('orders-unselect_all') : t('orders-select_all')"
                                            @click.prevent="toggleAll(grid.data)">
                                            <span class="toggle-all-icon">−</span>
                                        </button>
                                    </div>
                                </th>
                                <grid-th :grid="grid">{{ t('orders-table-id') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-date') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-status') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-value') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-client') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-phone') }}</grid-th>
                                <grid-th :grid="grid">{{ t('orders-table-email') }}</grid-th>
                                <th v-for="field in fields" :key="field.identifier">
                                    {{ field.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style="cursor: pointer" v-for="order in grid.data" :key="order.id"
                                :class="{ 'row-selected': isSelected(order.id) }" @click="
                                    $router.push(
                                        'client/' +
                                        order.clients_id +
                                        '#event-' +
                                        order.id
                                    )
                                    ">
                                <td v-if="user.global_admin" class="checkbox-col" @click.stop>
                                    <div class="checkbox-center-wrapper">
                                        <label class="kv-checkbox-wrapper" @click.stop>
                                            <input type="checkbox" :checked="isSelected(order.id)"
                                                @change="toggleOrder(order.id)" @click.stop />
                                            <span class="kv-checkbox-custom"></span>
                                        </label>
                                    </div>
                                </td>
                                <td>
                                    {{ order.id }} ({{ order.external_order_id }})
                                </td>
                                <td style="min-width: 150px">
                                    <human-date :date="order.created" :format="t('dates-format_date_time')" />
                                </td>
                                <td>{{ order.external_status }}</td>
                                <td>{{ order.value }}</td>
                                <td>{{ order.first_name }} {{ order.last_name }}</td>
                                <td style="min-width: 100px">{{ order.whatsapp }}</td>
                                <td>{{ order.email }}</td>
                                <td v-for="field in fields" :key="field.identifier + '-' + order.id">
                                    {{ presentFieldValue(order, field) }}
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </grid>

                <KvFloatingActionBar v-if="selectedOrders.length > 0" @cancel="clearSelection">
                    <span class="selection-count">
                        {{ t('orders-selection-count', [selectedOrders.length, selectedOrders.length > 1 ? 's' : '']) }}
                    </span>

                    <KvButton variant="filled" prepend-icon="ui-download-01" @click.prevent="exportSelected" :disabled="exporting" :loading="exporting">
                        {{ t('orders-selection-export') }}
                    </KvButton>
                    <KvPopup v-if="user.global_admin" placement="top right" min-width="200px">
                        <template #trigger="{ toggle }">
                            <KvButton variant="tonal" prepend-icon="ui-dots-vertical"
                                :aria-label="t('orders-more_actions')" @click="toggle" />
                        </template>
                        <template #content="{ toggle }">
                            <div class="kv-popup-list">
                                <KvButton alignment="left" prepend-icon="ui-trash-01" variant="tonal"
                                    @click="openDeleteSelected(); toggle()">
                                    {{ t('orders-selection-remove') }}
                                </KvButton>
                            </div>
                        </template>
                    </KvPopup>
                    <KvButton variant="outlined" @click.prevent="clearSelection">
                        {{ t('cancel') }}
                    </KvButton>
                </KvFloatingActionBar>
            </div>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi.js";
import Papa from "papaparse";
import saveData from "../helpers/saveData";
import Icon from "../components/icon/icon";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import KvFloatingActionBar from "../components/ui/kv-floating-action-bar.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvButton from "../components/form/kv-button.vue";
import ConfirmDelete from "./components/confirm-delete.vue";
import { formatInstantInPlaceTz } from "../helpers/datetime";

export default {
    components: { Icon, KvTooltip, KvFloatingActionBar, KvPopup, KvButton, ConfirmDelete },
    data() {
        return {
            filters: {},
            ready: false,
            extraFilters: [],
            exporting: false,
            fields: [], // <- campos extras vindos do backend
            selectedOrders: [],
            currentGridData: [],
            totalOrders: 0,
            deleteMode: null, // "all" | "selected"
            toggleAllFloating: false,
            toggleAllStyle: {},
            scrollFrame: null,
        };
    },
    computed: {
        user() {
            return this.$store.state.user;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["reports"]["options"]?.global_access;
        },
        deleteCount() {
            return this.deleteMode === "selected" ? this.selectedOrders.length : this.totalOrders;
        },
        allSelectedOnPage() {
            if (this.currentGridData.length === 0) return false;
            return this.currentGridData.every(order => this.selectedOrders.includes(order.id));
        },
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.scheduleFloatingUpdate, true);
        window.removeEventListener("resize", this.scheduleFloatingUpdate);
        cancelAnimationFrame(this.scrollFrame);
    },
    watch: {
        selectedOrders() {
            this.$nextTick(this.updateFloatingToggleAll);
        },
    },
    async mounted() {
        // Capture phase: scroll events do not bubble, and the scroll container may not be the window
        window.addEventListener("scroll", this.scheduleFloatingUpdate, true);
        window.addEventListener("resize", this.scheduleFloatingUpdate);
        const filterBar = this.$refs.filterBar;
        filterBar.addFilter("period");

        // Segmentos
        if (this.$module("segments")) {
            const segmentsRequest = await this.$http(keviApi.getOptions("segments"));
            const segments = segmentsRequest.body;
            if (segments) {
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t("orders-filters-client_segment"),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            }
        }

        // Status
        if (this.$module("order_status")) {
            const statusRequest = await this.$http(
                keviApi.getOptions("settings/external-order-status", "GET")
            );
            if (statusRequest.status === 200) {
                const status = statusRequest.body.status;
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t("orders-filters-order_status"),
                    name: "order-status",
                    options: {
                        options: status.map((s) => ({
                            value: s.id,
                            label: s.external_label,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            }
        }

        // Campos extras (etapa 1)
        const fieldsRequest = await this.$http(
            keviApi.getOptions("/order-fields", "GET")
        );
        if (fieldsRequest.status === 200 && Array.isArray(fieldsRequest.body)) {
            this.fields = fieldsRequest.body.map((f) => ({
                label: f.field_name,
                identifier: f.field_identifier,
                type: f.type
            }));
        }

        this.$nextTick(() => {
            this.ready = true;
        });
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        scheduleFloatingUpdate() {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = requestAnimationFrame(this.updateFloatingToggleAll);
        },

        updateFloatingToggleAll() {
            const cell = this.$refs.selectAllCell;
            const table = cell && cell.closest("table");
            if (!table) {
                this.toggleAllFloating = false;
                return;
            }

            const topOffset = window.innerWidth <= 700 ? 70 : 16;
            const cellRect = cell.getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            const shouldFloat = cellRect.top < topOffset && tableRect.bottom > topOffset + 40;

            this.toggleAllFloating = shouldFloat;
            this.toggleAllStyle = shouldFloat
                ? {
                    top: topOffset + "px",
                    left: cellRect.left + cellRect.width / 2 - 10 + "px",
                }
                : {};
        },

        updateFilters(filters) {
            this.filters = filters;
            this.clearSelection();
        },

        onGridLoaded(payload) {
            this.currentGridData = payload.data || [];
            this.totalOrders = payload.total || 0;
            this.clearSelection();
        },

        toggleOrder(id) {
            const index = this.selectedOrders.indexOf(id);
            if (index === -1) {
                this.selectedOrders.push(id);
            } else {
                this.selectedOrders.splice(index, 1);
            }
        },

        toggleAll(gridData) {
            if (this.allSelectedOnPage) {
                const pageIds = gridData.map(o => o.id);
                this.selectedOrders = this.selectedOrders.filter(id => !pageIds.includes(id));
            } else {
                gridData.forEach(order => {
                    if (!this.selectedOrders.includes(order.id)) {
                        this.selectedOrders.push(order.id);
                    }
                });
            }
        },

        isSelected(id) {
            return this.selectedOrders.includes(id);
        },

        clearSelection() {
            this.selectedOrders = [];
        },

        presentFieldValue(order, field, toCsv = false) {
            const value = order[field.identifier] || " ";

            if(field.type == "checkbox"){
                let parsed = [];
                try {
                    parsed = JSON.parse(value || "[]");
                // eslint-disable-next-line no-unused-vars
                } catch (e) {
                    parsed = [];
                }

                let texts = parsed.map(item => item.text);
                let combined = texts.join(", ");

                if(toCsv){
                    return `"${combined.replace(/"/g, "\"\"")}"`;
                }

                return combined;
            }

            return  value;
        },

        exportOrders() {
            const options = keviApi.getOptions("orders", "GET", {
                filters: this.filters,
                export: true,
            });

            this.exporting = true;

            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            const locale =
                (this.$store.state.place && this.$store.state.place.locale) ||
                "pt-BR";
            this.$http(options).then((response) => {
                const orders = response.body.data.map((order) => {
                    const formattedDate = order.created
                        ? formatInstantInPlaceTz(order.created, tz, locale)
                        : null;

                    let name = order.first_name || "";
                    if (order.last_name) {
                        name += " " + order.last_name;
                    }

                    const base = {
                        [this.t("orders-export_headers-id")]: order.id,
                        [this.t("orders-export_headers-date")]: formattedDate,
                        [this.t("orders-export_headers-status")]: order.external_status,
                        [this.t("orders-export_headers-value")]: order.value,
                        [this.t("orders-export_headers-client")]: name,
                        [this.t("orders-export_headers-phone")]: order.whatsapp,
                        [this.t("orders-export_headers-email")]: order.email,
                        [this.t("orders-export_headers-external_id")]: order.external_order_id,
                    };

                    this.fields.forEach((field) => {
                        base[field.label] = this.presentFieldValue(order, field, true) ;
                    });

                    return base;
                });

                const csv = Papa.unparse(orders);
                saveData(csv, "orders.csv");
                this.exporting = false;
            });
        },

        openDeleteAll() {
            this.deleteMode = "all";
            this.$refs.confirmDelete.open();
        },

        openDeleteSelected() {
            this.deleteMode = "selected";
            this.$refs.confirmDelete.open();
        },

        onConfirmDelete() {
            if (this.deleteMode === "selected") {
                this.deleteSelected();
            } else {
                this.deleteAll();
            }
        },

        deleteAll() {
            const options = keviApi.getOptions("/orders", "DELETE", {
                filters: this.filters,
            });

            this.ready = false;

            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.ready = true;
                    this.$store.dispatch("notification/success", this.t("orders-deleted_success"));
                }
            });
        },

        exportSelected() {
            const selectedData = this.currentGridData.filter(order =>
                this.selectedOrders.includes(order.id)
            );

            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            const locale =
                (this.$store.state.place && this.$store.state.place.locale) ||
                "pt-BR";

            const orders = selectedData.map((order) => {
                const formattedDate = order.created
                    ? formatInstantInPlaceTz(order.created, tz, locale)
                    : null;

                let name = order.first_name || "";
                if (order.last_name) {
                    name += " " + order.last_name;
                }

                const base = {
                    [this.t("orders-export_headers-id")]: order.id,
                    [this.t("orders-export_headers-date")]: formattedDate,
                    [this.t("orders-export_headers-status")]: order.external_status,
                    [this.t("orders-export_headers-value")]: order.value,
                    [this.t("orders-export_headers-client")]: name,
                    [this.t("orders-export_headers-phone")]: order.whatsapp,
                    [this.t("orders-export_headers-email")]: order.email,
                    [this.t("orders-export_headers-external_id")]: order.external_order_id,
                };

                this.fields.forEach((field) => {
                    base[field.label] = this.presentFieldValue(order, field, true);
                });

                return base;
            });

            const csv = Papa.unparse(orders);
            saveData(csv, "orders.csv");
            this.$store.dispatch("notification/success",
                this.t('orders-export_selected_success', [selectedData.length, selectedData.length > 1 ? 's' : '', selectedData.length > 1 ? 's' : ''])
            );
        },

        deleteSelected() {
            const options = keviApi.getOptions("/orders", "DELETE", {
                ids: [...this.selectedOrders],
            });

            this.ready = false;

            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.clearSelection();
                    this.ready = true;
                    this.$store.dispatch("notification/success",
                        this.t('orders-delete_selected_success', [response.body.total, response.body.total > 1 ? 's' : '', response.body.total > 1 ? 's' : ''])
                    );
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.checkbox-col {
    width: 44px;
    min-width: 44px;
    max-width: 44px;
    padding: 0 !important;
    vertical-align: middle !important;
}

.checkbox-center-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.toggle-all-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 4px;
    background: #2AAA4C;
    cursor: pointer;
    padding: 0;
    transition: background 0.15s ease;

    &:hover {
        background: #1e7e34;
    }

    &.floating {
        position: fixed;
        z-index: 999;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }

    .toggle-all-icon {
        color: #fff;
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
    }
}

tbody tr {
    .kv-checkbox-wrapper {
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    &:hover .kv-checkbox-wrapper,
    &.row-selected .kv-checkbox-wrapper {
        opacity: 1;
    }
}

.kv-checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
    padding: 4px;

    input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        pointer-events: none;
        width: 0;
        height: 0;

        &:checked + .kv-checkbox-custom {
            background: #2AAA4C;
            border-color: #2AAA4C;

            &::after {
                content: '';
                width: 5px;
                height: 9px;
                border: 2px solid #fff;
                border-top: none;
                border-left: none;
                transform: rotate(45deg);
                position: absolute;
                top: 1px;
            }
        }
    }

    .kv-checkbox-custom {
        width: 18px;
        height: 18px;
        border: 2px solid #D1D5DB;
        border-radius: 4px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        position: relative;
    }

    &:hover .kv-checkbox-custom {
        border-color: #2AAA4C;
    }
}

.row-selected td {
    background-color: #E8F5E9 !important;
}

.selection-count {
    display: inline-flex;
    align-items: center;
    background: #eaf7ee;
    color: #1e7e34;
    font-weight: 600;
    font-size: 13px;
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid #c8e6cf;
}

</style>
